import { Link } from 'react-router-dom';
import { AlertTriangle, Shield, TrendingUp, Clock, Eye, Settings } from 'lucide-react';
import { usePldDashboard, getSeverityLabel, getSeverityColor, getStatusLabel, getRuleTypeLabel } from '../../../hooks/usePld';

const severityBadgeClasses: Record<string, string> = {
  red: 'bg-red-500/20 text-red-400',
  orange: 'bg-orange-500/20 text-orange-400',
  blue: 'bg-blue-500/20 text-blue-400',
  yellow: 'bg-yellow-500/20 text-yellow-400',
  gray: 'bg-surface-600 text-surface-300',
};

export function PldDashboard() {
  const { data: dashboard, isLoading } = usePldDashboard();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-surface-600" />
        <p className="text-surface-400">Erro ao carregar dashboard</p>
      </div>
    );
  }

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('pt-BR');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">PLD - Prevenção à Lavagem de Dinheiro</h1>
          <p className="text-surface-400">Monitoramento e análise de alertas</p>
        </div>
        <div className="flex gap-3">
          <Link to="/admin/compliance/pld/config" className="flex items-center gap-2 px-4 py-2 bg-surface-700 hover:bg-surface-600 rounded-lg transition-colors">
            <Settings className="w-4 h-4" /> Configurações
          </Link>
          <Link to="/admin/compliance/pld/rules" className="flex items-center gap-2 px-4 py-2 bg-surface-700 hover:bg-surface-600 rounded-lg transition-colors">
            <Shield className="w-4 h-4" /> Regras
          </Link>
          <Link to="/admin/compliance/pld/alerts" className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
            <Eye className="w-4 h-4" /> Ver Alertas
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-surface-900 rounded-xl p-4 border border-surface-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/20 rounded-lg"><Clock className="w-5 h-5 text-yellow-400" /></div>
            <div>
              <p className="text-2xl font-bold">{dashboard.pendingAlerts}</p>
              <p className="text-sm text-surface-400">Pendentes</p>
            </div>
          </div>
        </div>
        <div className="bg-surface-900 rounded-xl p-4 border border-surface-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg"><AlertTriangle className="w-5 h-5 text-red-400" /></div>
            <div>
              <p className="text-2xl font-bold">{dashboard.alertsBySeverity?.CRITICAL || 0}</p>
              <p className="text-sm text-surface-400">Críticos</p>
            </div>
          </div>
        </div>
        <div className="bg-surface-900 rounded-xl p-4 border border-surface-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-500/20 rounded-lg"><Shield className="w-5 h-5 text-primary-400" /></div>
            <div>
              <p className="text-2xl font-bold">{dashboard.reviewedAlerts}</p>
              <p className="text-sm text-surface-400">Analisados</p>
            </div>
          </div>
        </div>
        <div className="bg-surface-900 rounded-xl p-4 border border-surface-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg"><TrendingUp className="w-5 h-5 text-purple-400" /></div>
            <div>
              <p className="text-2xl font-bold">{dashboard.escalatedAlerts}</p>
              <p className="text-sm text-surface-400">Escalados</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-surface-900 rounded-xl p-6 border border-surface-800">
          <h3 className="font-semibold mb-4">Alertas por Severidade</h3>
          <div className="space-y-3">
            {Object.entries(dashboard.alertsBySeverity || {}).map(([severity, count]) => (
              <div key={severity} className="flex items-center justify-between">
                <span className={`px-2 py-1 rounded text-xs font-medium ${severityBadgeClasses[getSeverityColor(severity)] || severityBadgeClasses.gray}`}>
                  {getSeverityLabel(severity)}
                </span>
                <span className="font-medium">{count as number}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-surface-900 rounded-xl p-6 border border-surface-800">
          <h3 className="font-semibold mb-4">Alertas por Tipo de Regra</h3>
          <div className="space-y-3">
            {Object.entries(dashboard.alertsByRuleType || {}).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <span className="text-sm text-surface-300">{getRuleTypeLabel(type)}</span>
                <span className="font-medium">{count as number}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Alerts */}
      <div className="bg-surface-900 rounded-xl border border-surface-800 overflow-hidden">
        <div className="px-6 py-4 border-b border-surface-800 flex justify-between items-center">
          <h3 className="font-semibold">Alertas Recentes</h3>
          <Link to="/admin/compliance/pld/alerts" className="text-sm text-primary-400 hover:text-primary-300">Ver todos</Link>
        </div>
        <table className="w-full">
          <thead className="bg-surface-800/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Data</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Regra</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Severidade</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-surface-400 uppercase">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-800">
            {dashboard.recentAlerts?.slice(0, 5).map((alert) => (
              <tr key={alert.id} className="hover:bg-surface-800/50 transition-colors">
                <td className="px-6 py-4 text-sm">{formatDate(alert.createdAt)}</td>
                <td className="px-6 py-4 text-sm">{alert.ruleName}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${severityBadgeClasses[getSeverityColor(alert.severity)] || severityBadgeClasses.gray}`}>
                    {getSeverityLabel(alert.severity)}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-surface-300">{getStatusLabel(alert.status)}</td>
                <td className="px-6 py-4 text-right">
                  <Link to={`/admin/compliance/pld/alerts/${alert.id}`} className="text-primary-400 hover:text-primary-300 text-sm">
                    Analisar
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PldDashboard;
